import React from 'react';

import { useIntl } from 'react-intl';

import { aiSuggestionsMessages } from '@atlaskit/editor-common/ai-messages';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import AppStack, { type AppStackItem, type AppStackItemProps } from '@atlassian/app-stack';
import {
	getLogoForSourceType,
	getSourceType,
} from '@atlassian/rovo-platform-ui-components/source-logo-stack/source-util';

import type { SuggestionSource } from '../../../aiSuggestionsPluginType';

export const SUGGESTION_SOURCE_APP_STACK_TESTID = 'ai-suggestion-source-app-stack';

/**
 * Caps how many logos are shown. The design shows two; a third leaves room for a genuinely
 * multi-app suggestion without the stack crowding the Details row it shares with the
 * Restricted lozenge.
 */
const MAX_VISIBLE_APPS = 3;

/** The source types `source-util` knows how to map to a logo. */
type SourceType = ReturnType<typeof getSourceType>;
type KnownSourceType = Exclude<SourceType, 'unknown'>;

const SOURCE_APP_NAMES: Record<KnownSourceType, string> = {
	bitbucket: 'Bitbucket',
	confluence: 'Confluence',
	goals: 'Goals',
	home: 'Home',
	jira: 'Jira',
	loom: 'Loom',
	projects: 'Projects',
	teams: 'Teams',
};

/** A logo resolved from a source, keyed by the source type it was resolved from. */
export type SuggestionSourceApp = {
	component: React.ComponentType<AppStackItemProps>;
	key: KnownSourceType;
	name: string;
};

/**
 * Resolves the apps behind a suggestion's sources, in source order and de-duplicated so
 * several sources in one app contribute a single logo.
 *
 * Resolution is delegated wholesale to `@atlassian/rovo-platform-ui-components`' `source-util`,
 * which keys off the source URL. A source without a URL therefore contributes no logo — the
 * same sources the details body already drops, since it can't link to them either.
 *
 * `source-util` covers Atlassian products only, so a third-party source (Google Drive, GitHub,
 * and so on) resolves to `unknown` and is skipped rather than given its `AtlassianIcon`
 * fallback, which would misattribute the source. The number of logos can therefore be lower
 * than the number of sources.
 */
export const getSuggestionSourceApps = (
	sources: SuggestionSource[] | undefined,
): SuggestionSourceApp[] => {
	const apps: SuggestionSourceApp[] = [];
	const seen = new Set<SourceType>();

	for (const { url } of sources ?? []) {
		if (!url) {
			continue;
		}

		const sourceType = getSourceType(url);

		if (sourceType === 'unknown' || seen.has(sourceType)) {
			continue;
		}

		seen.add(sourceType);
		apps.push({
			// `source-util` types its logos as `ElementType<LogoProps>`; `AppStackItemProps` is a
			// subset of `LogoProps`, so the props `AppStack` passes are all accepted.
			component: getLogoForSourceType(sourceType) as React.ComponentType<AppStackItemProps>,
			key: sourceType,
			name: SOURCE_APP_NAMES[sourceType],
		});
	}

	return apps;
};

export type SuggestionSourceAppStackProps = {
	sources: SuggestionSource[] | undefined;
};

/**
 * Logos of the apps a suggestion's sources came from, de-duplicated so several sources in one
 * app contribute a single logo. Renders nothing when no source resolves to a known app.
 */
export function SuggestionSourceAppStack({
	sources,
}: SuggestionSourceAppStackProps): React.JSX.Element | null {
	const { formatList, formatMessage } = useIntl();
	const sourceApps = React.useMemo(() => getSuggestionSourceApps(sources), [sources]);
	// The list itself provides the accessible name, so the product logos are decorative.
	const data: AppStackItem[] = React.useMemo(
		() => sourceApps.map(({ component }) => ({ component, label: '' })),
		[sourceApps],
	);

	if (data.length === 0) {
		return null;
	}

	const sourceAppsLabel = formatMessage(aiSuggestionsMessages.cardSourceAppsTooltip, {
		apps: formatList(sourceApps.map(({ name }) => name)),
	});

	return (
		<Tooltip content={sourceAppsLabel} position="top" ignoreTooltipPointerEvents>
			<AppStack
				data={data}
				// `xsmall` is the design's 20px tile, and gives the -4px overlap it shows.
				size="xsmall"
				maxCount={MAX_VISIBLE_APPS}
				label={sourceAppsLabel}
				testId={SUGGESTION_SOURCE_APP_STACK_TESTID}
			/>
		</Tooltip>
	);
}
